import { useEffect, useRef, useCallback, useMemo } from 'react';
import * as THREE from 'three';

const BOUNDARY_CENTER = new THREE.Vector3(0, 0, 0);

export const useFreeFlightControls = (canvasRef, cameraRef, configArg) => {

  const configRef = useRef({
    movementSpeed: 0.015,
    zoomSpeed: 1.0,
    orbitSpeed: 0.008,
    initialRadius: 3.2,
    minRadius: 1.0,
    maxRadius: 60,
    boundaryRadius: 55,
  });

  Object.assign(configRef.current, configArg);

  const orbitRef = useRef({
    theta: 25 * Math.PI / 180,    
    phi: 0,                        
    radius: configRef.current.initialRadius,
    target: new THREE.Vector3(0, 0, 0),
  });

  const moveState = useRef({
    forward: false, backward: false,
    left: false, right: false,
    up: false, down: false,
    rotateLeft: false, rotateRight: false,
    pitchForward: false, pitchBackward: false,
  });

  const moveVelocity = useRef({
    forward: 0, backward: 0,
    left: 0, right: 0,
    up: 0, down: 0,
  });

  const rotationVelocity = useRef({
    yawLeft: 0, yawRight: 0,
    pitchForward: 0, pitchBackward: 0,
  });

  const mouseState = useRef({
    dragging: false, lastX: 0, lastY: 0,
    touchDistance: 0, hasMoved: false, totalMovement: 0,
    activeTouches: 0,
  });

  const zoomVelocity = useRef(0);
  const orbitMomentum = useRef({ theta: 0, phi: 0 });
  const hasInitialized = useRef(false);
  const lastInteractionRef = useRef(0);
  const idleResetFired = useRef(true);

  const joystickRef = useRef({ x: 0, y: 0 });
  const rotationJoystickRef = useRef({ x: 0, y: 0 });

  const flyRef = useRef({
    active: false, time: 0, duration: 1.0,
    startTheta: 0, startPhi: 0, startRadius: 7.0,
    startTarget: new THREE.Vector3(),
    endTheta: 0, endPhi: 0, endRadius: 7.0,
    endTarget: new THREE.Vector3(),
  });

  const markInteraction = useCallback(() => {
    lastInteractionRef.current = performance.now();
    idleResetFired.current = false;
  }, []);

  const applyCameraOrbit = useCallback((camera) => {
    const { theta, phi, radius, target } = orbitRef.current;

    const qTheta = new THREE.Quaternion().setFromAxisAngle(new THREE.Vector3(0, 1, 0), theta);
    const qPhi = new THREE.Quaternion().setFromAxisAngle(new THREE.Vector3(1, 0, 0), phi);
    const combined = new THREE.Quaternion().multiplyQuaternions(qTheta, qPhi);

    const offset = new THREE.Vector3(0, 0, radius).applyQuaternion(combined);
    camera.position.copy(target).add(offset);

    const up = new THREE.Vector3(0, 1, 0).applyQuaternion(combined);
    camera.up.copy(up);
    camera.lookAt(target);
  }, []);

  const initCamera = useCallback(() => {
    const camera = cameraRef.current;
    if (!camera || hasInitialized.current) return;
    hasInitialized.current = true;

    const orbit = orbitRef.current;
    const offset = new THREE.Vector3().subVectors(camera.position, orbit.target);
    orbit.radius = offset.length();
    if (orbit.radius > 0.001) {
      orbit.phi = Math.asin(Math.max(-1, Math.min(1, -offset.y / orbit.radius)));
      orbit.theta = Math.atan2(offset.x, offset.z);
    }
  }, [cameraRef]);

  const resetCamera = useCallback(() => {
    const camera = cameraRef.current;
    if (!camera) return;

    const cfg = configRef.current;
    const orbit = orbitRef.current;

    orbitMomentum.current.theta = 0;
    orbitMomentum.current.phi = 0;

    const fly = flyRef.current;
    fly.startTheta = orbit.theta;
    fly.startPhi = orbit.phi;
    fly.startRadius = orbit.radius;
    fly.startTarget.copy(orbit.target);
    fly.endTheta = 25 * Math.PI / 180;
    fly.endPhi = 0;
    fly.endRadius = cfg.initialRadius;
    fly.endTarget.set(0, 0, 0);
    fly.duration = 1.5;
    fly.time = 0;
    fly.active = true;
  }, [cameraRef]);

  const flyTo = useCallback((camPos, lookAtPos, duration = 1.0) => {
    const camera = cameraRef.current;
    if (!camera) return;

    const orbit = orbitRef.current;
    const fly = flyRef.current;

    fly.startTheta = orbit.theta;
    fly.startPhi = orbit.phi;
    fly.startRadius = orbit.radius;
    fly.startTarget.copy(orbit.target);

    const endOffset = new THREE.Vector3().subVectors(camPos, lookAtPos);
    const endRadius = endOffset.length();
    fly.endRadius = endRadius;
    fly.endTarget.copy(lookAtPos);
    if (endRadius > 0.001) {
      fly.endPhi = Math.asin(Math.max(-1, Math.min(1, -endOffset.y / endRadius)));
      fly.endTheta = Math.atan2(endOffset.x, endOffset.z);
    } else {
      fly.endTheta = orbit.theta;
      fly.endPhi = orbit.phi;
    }

    fly.duration = duration;
    fly.time = 0;
    fly.active = true;
  }, [cameraRef]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;
      if (e.ctrlKey || e.metaKey || e.altKey) return;
      markInteraction();

      switch (e.key.toLowerCase()) {
        case 'w': moveState.current.forward = true; break;
        case 's': moveState.current.backward = true; break;
        case 'a': moveState.current.left = true; break;
        case 'd': moveState.current.right = true; break;
        case 'arrowup': e.preventDefault(); moveState.current.forward = true; break;
        case 'arrowdown': e.preventDefault(); moveState.current.backward = true; break;
        case 'arrowleft': e.preventDefault(); moveState.current.left = true; break;
        case 'arrowright': e.preventDefault(); moveState.current.right = true; break;
        case 'q': moveState.current.rotateLeft = true; break;
        case 'e': moveState.current.rotateRight = true; break;
        case 'r': moveState.current.pitchForward = true; break;
        case 'f': moveState.current.pitchBackward = true; break;
        case 'z':
        case ' ': e.preventDefault(); moveState.current.up = true; break;
        case 'x': moveState.current.down = true; break;
        default: break;
      }
    };

    const handleKeyUp = (e) => {
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;
      if (e.ctrlKey || e.metaKey || e.altKey) return;

      switch (e.key.toLowerCase()) {
        case 'w': moveState.current.forward = false; break;
        case 's': moveState.current.backward = false; break;
        case 'a': moveState.current.left = false; break;
        case 'd': moveState.current.right = false; break;
        case 'arrowup': moveState.current.forward = false; break;
        case 'arrowdown': moveState.current.backward = false; break;
        case 'arrowleft': moveState.current.left = false; break;
        case 'arrowright': moveState.current.right = false; break;
        case 'q': moveState.current.rotateLeft = false; break;
        case 'e': moveState.current.rotateRight = false; break;
        case 'r': moveState.current.pitchForward = false; break;
        case 'f': moveState.current.pitchBackward = false; break;
        case 'z':
        case ' ': moveState.current.up = false; break;
        case 'x': moveState.current.down = false; break;
        default: break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
    };
  }, [markInteraction]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const handlePointerDown = (e) => {
      if (e.pointerType === 'touch') return;
      if (e.target !== canvas) return;
      markInteraction();
      mouseState.current.dragging = true;
      mouseState.current.hasMoved = false;
      mouseState.current.totalMovement = 0;
      mouseState.current.lastX = e.clientX;
      mouseState.current.lastY = e.clientY;
      orbitMomentum.current.theta = 0;
      orbitMomentum.current.phi = 0;
    };

    const handlePointerMove = (e) => {
      if (e.pointerType === 'touch') return;
      if (!mouseState.current.dragging) return;
      const cfg = configRef.current;

      const deltaX = e.clientX - mouseState.current.lastX;
      const deltaY = e.clientY - mouseState.current.lastY;
      mouseState.current.totalMovement += Math.abs(deltaX) + Math.abs(deltaY);

      if (mouseState.current.totalMovement > 3) {
        mouseState.current.hasMoved = true;

        const dTheta = -deltaX * cfg.orbitSpeed;
        const dPhi = deltaY * cfg.orbitSpeed;

        orbitRef.current.theta += dTheta;
        orbitRef.current.phi += dPhi;

        orbitMomentum.current.theta = orbitMomentum.current.theta * 0.5 + dTheta * 0.5;
        orbitMomentum.current.phi = orbitMomentum.current.phi * 0.5 + dPhi * 0.5;
      }

      mouseState.current.lastX = e.clientX;
      mouseState.current.lastY = e.clientY;
    };

    const handlePointerUp = (e) => {
      if (e.pointerType === 'touch') return;
      mouseState.current.dragging = false;
    };

    const handleTouchStart = (e) => {
      if (e.target !== canvas) return;
      markInteraction();
      mouseState.current.activeTouches = e.touches.length;

      if (e.touches.length === 2) {
        const dx = e.touches[0].clientX - e.touches[1].clientX;
        const dy = e.touches[0].clientY - e.touches[1].clientY;
        mouseState.current.touchDistance = Math.sqrt(dx * dx + dy * dy);
        mouseState.current.dragging = false;
        return;
      }

      mouseState.current.dragging = true;
      mouseState.current.hasMoved = false;
      mouseState.current.totalMovement = 0;
      mouseState.current.lastX = e.touches[0].clientX;
      mouseState.current.lastY = e.touches[0].clientY;
      orbitMomentum.current.theta = 0;
      orbitMomentum.current.phi = 0;
    };

    const handleTouchMove = (e) => {
      e.preventDefault();
      const cfg = configRef.current;

      if (e.touches.length === 2) {
        const dx = e.touches[0].clientX - e.touches[1].clientX;
        const dy = e.touches[0].clientY - e.touches[1].clientY;
        const distance = Math.sqrt(dx * dx + dy * dy);
        const delta = distance - mouseState.current.touchDistance;
        orbitRef.current.radius = Math.max(cfg.minRadius, Math.min(cfg.maxRadius,
          orbitRef.current.radius * (1 - delta * 0.006)
        ));
        mouseState.current.touchDistance = distance;
        return;
      }

      if (!mouseState.current.dragging || e.touches.length !== 1) return;

      const clientX = e.touches[0].clientX;
      const clientY = e.touches[0].clientY;
      const deltaX = clientX - mouseState.current.lastX;
      const deltaY = clientY - mouseState.current.lastY;
      mouseState.current.totalMovement += Math.abs(deltaX) + Math.abs(deltaY);

      if (mouseState.current.totalMovement > 3) {
        mouseState.current.hasMoved = true;

        const sensitivity = cfg.orbitSpeed * 4.0;
        const dTheta = -deltaX * sensitivity;
        const dPhi = deltaY * sensitivity;

        orbitRef.current.theta += dTheta;
        orbitRef.current.phi += dPhi;

        orbitMomentum.current.theta = orbitMomentum.current.theta * 0.5 + dTheta * 0.5;
        orbitMomentum.current.phi = orbitMomentum.current.phi * 0.5 + dPhi * 0.5;
      }

      mouseState.current.lastX = clientX;
      mouseState.current.lastY = clientY;
    };

    const handleTouchEnd = () => {
      mouseState.current.activeTouches = 0;
      mouseState.current.dragging = false;
    };

    const handleWheel = (e) => {
      e.preventDefault();
      markInteraction();
      zoomVelocity.current += e.deltaY * 0.001 * configRef.current.zoomSpeed;
    };

    canvas.addEventListener('pointerdown', handlePointerDown);
    canvas.addEventListener('pointermove', handlePointerMove);
    canvas.addEventListener('pointerup', handlePointerUp);
    canvas.addEventListener('pointerleave', handlePointerUp);
    canvas.addEventListener('wheel', handleWheel, { passive: false });
    canvas.addEventListener('touchstart', handleTouchStart, { passive: false });
    canvas.addEventListener('touchmove', handleTouchMove, { passive: false });
    canvas.addEventListener('touchend', handleTouchEnd, { passive: false });

    return () => {
      canvas.removeEventListener('pointerdown', handlePointerDown);
      canvas.removeEventListener('pointermove', handlePointerMove);
      canvas.removeEventListener('pointerup', handlePointerUp);
      canvas.removeEventListener('pointerleave', handlePointerUp);
      canvas.removeEventListener('wheel', handleWheel);
      canvas.removeEventListener('touchstart', handleTouchStart);
      canvas.removeEventListener('touchmove', handleTouchMove);
      canvas.removeEventListener('touchend', handleTouchEnd);
    };
  }, [canvasRef, markInteraction]);

  const easeInOutCubic = (t) => t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;

  const update = useCallback((delta) => {
    const camera = cameraRef.current;
    if (!camera || document.hidden) return;

    const cfg = configRef.current;
    const orbit = orbitRef.current;

    const fly = flyRef.current;
    if (fly.active) {
      fly.time += delta;
      const p = Math.min(fly.time / fly.duration, 1);
      const ep = easeInOutCubic(p);

      orbit.theta = fly.startTheta + (fly.endTheta - fly.startTheta) * ep;
      orbit.phi = fly.startPhi + (fly.endPhi - fly.startPhi) * ep;
      orbit.radius = fly.startRadius + (fly.endRadius - fly.startRadius) * ep;
      orbit.target.lerpVectors(fly.startTarget, fly.endTarget, ep);

      applyCameraOrbit(camera);

      if (p >= 1) fly.active = false;
      return;
    }

    const rotSpeed = 1.5 * delta * 0.377;
    const rotAccel = 0.15;
    const rotDecel = 0.08;

    const targetRotVel = {
      yawLeft: moveState.current.rotateLeft ? 1 : 0,
      yawRight: moveState.current.rotateRight ? 1 : 0,
      pitchForward: moveState.current.pitchForward ? 1 : 0,
      pitchBackward: moveState.current.pitchBackward ? 1 : 0,
    };

    Object.keys(rotationVelocity.current).forEach(key => {
      const lf = targetRotVel[key] > 0 ? rotAccel : rotDecel;
      rotationVelocity.current[key] += (targetRotVel[key] - rotationVelocity.current[key]) * lf;
    });

    if (rotationVelocity.current.yawLeft > 0.01) {
      orbit.theta += rotSpeed * rotationVelocity.current.yawLeft;
    }
    if (rotationVelocity.current.yawRight > 0.01) {
      orbit.theta -= rotSpeed * rotationVelocity.current.yawRight;
    }
    if (rotationVelocity.current.pitchForward > 0.01) {
      orbit.phi -= rotSpeed * 0.5 * rotationVelocity.current.pitchForward;
    }
    if (rotationVelocity.current.pitchBackward > 0.01) {
      orbit.phi += rotSpeed * 0.5 * rotationVelocity.current.pitchBackward;
    }

    const rj = rotationJoystickRef.current;
    if (rj.x !== 0 || rj.y !== 0) {
      orbit.theta -= rj.x * delta * 0.6;
      orbit.phi += rj.y * delta * 0.3;
    }

    const mom = orbitMomentum.current;
    if (Math.abs(mom.theta) > 0.00001 || Math.abs(mom.phi) > 0.00001) {
      if (!mouseState.current.dragging) {
        orbit.theta += mom.theta;
        orbit.phi += mom.phi;
        mom.theta *= 0.92;
        mom.phi *= 0.92;
      }
    }

    if (Math.abs(zoomVelocity.current) > 0.001) {
      orbit.radius = Math.max(cfg.minRadius, Math.min(cfg.maxRadius,
        orbit.radius * (1 + zoomVelocity.current)
      ));
      zoomVelocity.current *= 0.9;
    }

    const camForward = new THREE.Vector3(
      -Math.sin(orbit.theta), 0, -Math.cos(orbit.theta)
    ).normalize();
    const camRight = new THREE.Vector3(
      -Math.cos(orbit.theta), 0, Math.sin(orbit.theta)
    ).normalize();

    const speed = cfg.movementSpeed * delta * 60;
    const accel = 0.25;
    const decel = 0.12;

    const targetVel = {
      forward: moveState.current.forward ? 1 : 0,
      backward: moveState.current.backward ? 1 : 0,
      left: moveState.current.left ? 1 : 0,
      right: moveState.current.right ? 1 : 0,
      up: moveState.current.up ? 1 : 0,
      down: moveState.current.down ? 1 : 0,
    };

    Object.keys(moveVelocity.current).forEach(key => {
      const lf = targetVel[key] > 0 ? accel : decel;
      moveVelocity.current[key] += (targetVel[key] - moveVelocity.current[key]) * lf;
    });

    const mv = moveVelocity.current;
    const translation = new THREE.Vector3();
    translation.add(camForward.clone().multiplyScalar((mv.forward - mv.backward) * speed * 1.0));
    translation.add(camRight.clone().multiplyScalar((mv.right - mv.left) * speed * 1.0));
    translation.y += (mv.up - mv.down) * speed * 1.0;

    const mj = joystickRef.current;
    if (mj.x !== 0 || mj.y !== 0) {
      translation.add(camForward.clone().multiplyScalar(mj.y * speed * 0.25));
      translation.add(camRight.clone().multiplyScalar(-mj.x * speed * 0.25));
    }

    orbit.target.add(translation);

    const dist = orbit.target.distanceTo(BOUNDARY_CENTER);
    if (dist > cfg.boundaryRadius) {
      orbit.target.sub(BOUNDARY_CENTER).normalize().multiplyScalar(cfg.boundaryRadius).add(BOUNDARY_CENTER);
    }

    applyCameraOrbit(camera);
  }, [cameraRef, applyCameraOrbit]);

  const onIdleResetRef = useRef(null);

  return useMemo(() => ({
    update,
    initCamera,
    flyTo,
    resetCamera,
    joystickRef,
    rotationJoystickRef,
    orbitRef,
    onIdleResetRef,
  }), [update, initCamera, flyTo, resetCamera]);
};
